import type { GroupID, InvType } from "./types";

/**
 * BiS slot groups, mirroring the addon's `TBR.BIS_SLOTS`.
 * One UI row per entry. Multi-instance groups (RING, TRINKET) require
 * BOTH equipped slots to be Myth-track before the group is "satisfied".
 */
export interface BiSSlotDef {
  id: GroupID;
  label: string;
  catalyzable: boolean;
  invTypes: InvType[];
  /** How many equipped positions exist for this group (1 or 2). */
  positions: 1 | 2;
}

export const BIS_SLOTS: BiSSlotDef[] = [
  { id: "HEAD",     label: "Head",      catalyzable: true,  positions: 1, invTypes: ["INVTYPE_HEAD"] },
  { id: "NECK",     label: "Neck",      catalyzable: false, positions: 1, invTypes: ["INVTYPE_NECK"] },
  { id: "SHOULDER", label: "Shoulder",  catalyzable: true,  positions: 1, invTypes: ["INVTYPE_SHOULDER"] },
  { id: "BACK",     label: "Back",      catalyzable: true,  positions: 1, invTypes: ["INVTYPE_CLOAK"] },
  { id: "CHEST",    label: "Chest",     catalyzable: true,  positions: 1, invTypes: ["INVTYPE_CHEST"] },
  { id: "WRIST",    label: "Wrist",     catalyzable: true,  positions: 1, invTypes: ["INVTYPE_WRIST"] },
  { id: "HANDS",    label: "Hands",     catalyzable: true,  positions: 1, invTypes: ["INVTYPE_HAND"] },
  { id: "BELT",     label: "Belt",      catalyzable: true,  positions: 1, invTypes: ["INVTYPE_WAIST"] },
  { id: "LEGS",     label: "Legs",      catalyzable: true,  positions: 1, invTypes: ["INVTYPE_LEGS"] },
  { id: "FEET",     label: "Feet",      catalyzable: true,  positions: 1, invTypes: ["INVTYPE_FEET"] },
  { id: "RING",     label: "Ring",      catalyzable: false, positions: 2, invTypes: ["INVTYPE_FINGER"] },
  { id: "TRINKET",  label: "Trinket",   catalyzable: false, positions: 2, invTypes: ["INVTYPE_TRINKET"] },
  {
    id: "MAINHAND", label: "Main Hand", catalyzable: false, positions: 1,
    invTypes: ["INVTYPE_WEAPON", "INVTYPE_2HWEAPON", "INVTYPE_WEAPONMAINHAND", "INVTYPE_RANGED"],
  },
  {
    id: "OFFHAND",  label: "Off Hand",  catalyzable: false, positions: 1,
    invTypes: ["INVTYPE_WEAPONOFFHAND", "INVTYPE_HOLDABLE", "INVTYPE_SHIELD"],
  },
];

export const SLOT_TO_GROUP: Record<string, GroupID> = (() => {
  const m: Record<string, GroupID> = {};
  for (const g of BIS_SLOTS) {
    for (const inv of g.invTypes) m[inv] = g.id;
  }
  return m;
})();

export const GROUP_BY_ID: Record<GroupID, BiSSlotDef> = Object.fromEntries(
  BIS_SLOTS.map((g) => [g.id, g]),
) as Record<GroupID, BiSSlotDef>;

export function groupForSlot(invType: InvType): GroupID | undefined {
  return SLOT_TO_GROUP[invType];
}

export function isCatalyzableGroup(groupID: GroupID): boolean {
  return GROUP_BY_ID[groupID]?.catalyzable ?? false;
}

/** Canonicalize INVTYPE_ROBE → INVTYPE_CHEST (cloth chest variant). */
export function canonicalSlot(invType: string): InvType {
  if (invType === "INVTYPE_ROBE") return "INVTYPE_CHEST";
  if (invType === "INVTYPE_RANGEDRIGHT") return "INVTYPE_RANGED";
  return invType as InvType;
}
